import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, type FC } from "react";

import ChevronRightIc from "/public/icon/chevron-right.svg";
import { UserPlan } from "@/apis/clients";
import { useUserAccount } from "@/states/auth/selectoers";

import {
  REFERRAL_NOTICE_ICON,
  REFERRAL_NOTICE_TONE,
  getReferralDismissalState,
  isReferralNoticeDismissed,
  saveReferralDismissalState,
  selectReferralNotice,
} from "./referralNotice";
import type { ReferralNoticeKind, ReferralNoticeSurface } from "./types";
import { useReferralOverview } from "./useReferralOverview";

type Props = {
  surface: ReferralNoticeSurface;
  kind?: ReferralNoticeKind;
  className?: string;
};

export const RewardNotice: FC<Props> = ({ surface, kind, className }) => {
  const { data } = useReferralOverview();
  const account = useUserAccount();
  const isFreeOrTrialPlan = account
    ? account.plan === UserPlan.FREE || account.plan === UserPlan.FREE_TRIAL
    : undefined;
  const notice = data
    ? selectReferralNotice(
        data,
        surface,
        account?.billing_use_end_date,
        isFreeOrTrialPlan,
      )
    : null;
  const visibleNotice = kind && notice?.kind !== kind ? null : notice;
  const [dismissalChecked, setDismissalChecked] = useState(false);
  const [dismissedEventId, setDismissedEventId] = useState<string | null>(null);

  useEffect(() => {
    const eventId = visibleNotice?.eventId ?? null;
    setDismissalChecked(false);
    setDismissedEventId(null);

    if (!eventId) {
      setDismissalChecked(true);
      return;
    }

    const dismissed = getReferralDismissalState(window.localStorage, eventId);
    setDismissedEventId(dismissed ? eventId : null);
    setDismissalChecked(true);
  }, [visibleNotice?.eventId]);

  if (
    !visibleNotice ||
    (visibleNotice.eventId && !dismissalChecked) ||
    isReferralNoticeDismissed(visibleNotice.eventId, dismissedEventId)
  ) {
    return null;
  }

  const icon =
    visibleNotice.icon === REFERRAL_NOTICE_ICON.CROWN
      ? {
          src: "/user/img/referral/referral-notice-crown.svg",
          width: 16,
          height: 15,
          className: "mt-px h-[15px] w-4 shrink-0 md:mt-0",
        }
      : {
          src: "/user/img/referral/referral-heart-filled.png",
          width: 18,
          height: 14,
          className: "mt-px h-[14px] w-auto shrink-0 object-contain md:mt-0",
        };
  const isGold = visibleNotice.tone === REFERRAL_NOTICE_TONE.GOLD;

  const dismiss = () => {
    if (!visibleNotice.eventId) return;

    if (!saveReferralDismissalState(window.localStorage, visibleNotice.eventId))
      return;

    setDismissedEventId(visibleNotice.eventId);
  };

  return (
    <aside
      data-referral-notice={visibleNotice.kind}
      className={`${
        className ?? "mb-6"
      } flex min-h-[37px] flex-col gap-2 rounded-lg border px-4 py-[9px] text-xs leading-[15px] md:flex-row md:items-center md:gap-[10px] ${
        isGold
          ? "border-[#D49728] bg-[#FAF6E6] text-[#1F1F1F]"
          : "border-[#41C9B5] bg-[#ECFAF7] text-[#1F1F1F]"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2 md:items-center">
        <Image
          src={icon.src}
          alt=""
          aria-hidden="true"
          width={icon.width}
          height={icon.height}
          className={icon.className}
        />
        <p className="min-w-0">
          <span
            className={`mr-2 font-bold ${
              isGold ? "text-[#D49728]" : "text-[#41BFAE]"
            }`}
          >
            {visibleNotice.title}
          </span>
          <span>{visibleNotice.body}</span>
          {visibleNotice.cta && (
            <Link
              href={visibleNotice.cta.href}
              className="ml-2 inline-flex items-center gap-1 align-middle whitespace-nowrap font-bold text-[#41BFAE]"
            >
              <ChevronRightIc width="24" fill="#41C9B4" className="shrink-0" />
              {visibleNotice.cta.label}
            </Link>
          )}
        </p>
      </div>

      {visibleNotice.dismissible && (
        <button
          type="button"
          aria-label="紹介特典のお知らせを閉じる"
          className="-my-1 -mr-1 h-5 w-5 shrink-0 text-base leading-5 text-[#757575]"
          onClick={dismiss}
        >
          ×
        </button>
      )}
    </aside>
  );
};
